(() => {
  if (customElements.get('belinter-shipping-progress')) return;
  const elements = new Set();
  let timer;
  let request;
  let revision = 0;

  const refresh = async () => {
    if (request || !elements.size) return;
    const version = revision;
    request = (async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('cart');
        const cart = await response.json();
        if (version === revision) elements.forEach(element => element.update(cart));
      } catch {
        elements.forEach(element => element.unavailable());
      } finally {
        request = null;
        if (version !== revision) schedule();
      }
    })();
    await request;
  };

  const schedule = () => {
    revision += 1;
    clearTimeout(timer);
    timer = setTimeout(refresh, 180);
  };

  class ShippingProgress extends HTMLElement {
    connectedCallback() {
      this.copy = JSON.parse(this.querySelector('[data-shipping-copy]').textContent);
      this.kits = new Set(this.dataset.kitVariants.split(','));
      this.threshold = Number(this.dataset.threshold);
      this.titleNode = this.querySelector('[data-shipping-title]');
      this.note = this.querySelector('[data-shipping-note]');
      this.meter = this.querySelector('progress');
      this.amount = this.querySelector('[data-shipping-amount]');
      this.cta = this.querySelector('[data-shipping-cta]');
      elements.add(this);
      schedule();
    }

    disconnectedCallback() {
      elements.delete(this);
    }

    update(cart) {
      const items = (cart.items || []).filter(item => item.requires_shipping !== false);
      const kitCount = items.filter(item => this.kits.has(String(item.variant_id || item.id))).length;
      const onlyKits = items.length > 0 && kitCount === items.length;
      const currency = cart.currency || 'COP';
      const total = Math.max(0, Number(cart.total_price || 0));
      const reached = currency === 'COP' && total >= this.threshold;
      const empty = items.length === 0;
      const money = value => new Intl.NumberFormat('es-CO', {
        style: 'currency', currency: 'COP', maximumFractionDigits: 0
      }).format(Math.max(0, Math.ceil(value / 100)));
      const interpolate = (text, values) => text.replace(/\[\[(\w+)\]\]/g, (_, key) => values[key] || '');
      const values = { amount: money(this.threshold - total), threshold: money(this.threshold) };
      let title;
      let note;
      if (onlyKits) {
        title = this.copy.kits_title;
        note = interpolate(this.copy.kits_note, values);
      } else if (currency !== 'COP') {
        title = this.copy.generic_title;
        note = this.copy.generic_note;
      } else if (reached) {
        title = this.copy.reached_title;
        note = this.copy.reached_note;
      } else if (empty) {
        title = this.copy.empty_title;
        note = this.copy.empty_note;
      } else {
        title = interpolate(this.copy.remaining_title, values);
        note = kitCount ? this.copy.mixed_note : this.copy.remaining_note;
      }
      this.titleNode.textContent = title;
      this.note.textContent = note;
      this.meter.hidden = onlyKits || currency !== 'COP';
      this.meter.value = Math.min(100, total / this.threshold * 100);
      this.amount.hidden = onlyKits || currency !== 'COP';
      this.amount.textContent = interpolate(this.copy.amount_label, { current: money(total), threshold: money(this.threshold) });
      this.cta.hidden = reached || onlyKits || currency !== 'COP';
      this.dataset.state = onlyKits || reached ? 'complete' : 'pending';
    }

    unavailable() {
      this.titleNode.textContent = this.copy.generic_title;
      this.note.textContent = this.copy.generic_note;
      this.meter.hidden = true;
      this.amount.hidden = true;
      this.cta.hidden = false;
    }
  }

  customElements.define('belinter-shipping-progress', ShippingProgress);
  const cartSelector = '.previewCart-wrapper, [data-cart-content], [data-cart-total], .wrapper-cartCount';
  const contentObserver = new MutationObserver(records => {
    if (records.some(record => {
      const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
      return !target?.closest('belinter-shipping-progress') && target?.closest(cartSelector);
    })) schedule();
  });
  contentObserver.observe(document.body, { childList: true, characterData: true, subtree: true });
  let drawerOpen = document.body.classList.contains('cart-sidebar-show');
  new MutationObserver(() => {
    const open = document.body.classList.contains('cart-sidebar-show');
    if (open && !drawerOpen) schedule();
    drawerOpen = open;
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  ['cart:updated', 'cart:refresh', 'shopify:section:load'].forEach(name => document.addEventListener(name, schedule));
})();

